import {
	Box,
	Button,
	Table,
	Group,
	Text,
	useMantineTheme,
	Skeleton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Utils } from "../javascript/utils";
import { SessionContext } from "@/context/SessionContext";
import { IconEye } from "@tabler/icons-react";
import React, { useEffect, useState, useContext } from "react";
import { ProjectSubmission } from "@/javascript/project-objects";

const CW = {
	desktop: {
		submissionDate: "25%",
		studentName: "25%",
		projectName: "25%",
		watchVideo: "30%",
	},
	mobile: {
		submissionDate: "33%",
		names: "33%",
		watchVideo: "33%",
	},
};

const FinishedVideos: React.FC = () => {
	const [mySubmissions, setMySubmissions] = useState<ProjectSubmission[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;
	const fzMed = isMobile ? 14 : 18;

	const HEADER_FW = 700;

	const session = useContext(SessionContext);
	if (!session) {
		return null;
	}

	const skeletonCell = <Skeleton height={20} ta="center" />;
	const skeletonRow = (
		<Table.Tr bg={theme.colors.blue[1]}>
			<Table.Td fz={fzSmall} w={"25%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"25%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"25%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"25%"} ta="center">
				{skeletonCell}
			</Table.Td>
		</Table.Tr>
	);

	const skeletonRows = Array.from({ length: 4 }, (_, i) =>
		React.cloneElement(skeletonRow, { key: `s${i}` })
	);

	// const [videoRowData, setVideoRowData] = useState<VideoRowData[]>([]);
	// filtering submissions down to only the ones with tasks containing userID
	// TODO - we do the same filtering in the mytasks component, maybe we do it once
	// then pass it down?
	useEffect(() => {
		setIsLoading(true);
		if (!session.projectSubmissions) {
			return;
		}
		const today = new Date();
		setMySubmissions(
			(session.projectSubmissions
				? session.projectSubmissions.filter(
						(submission) =>
							submission.dateFulfilled != null &&
							new Date(submission.editorDueDate) < today &&
							submission.instrumenatlistTasks.some(
								(task) => task.instrumentalist.id === session.user?.id
							)
				  )
				: []
			).sort((a, b) => b.dateSubmitted.getTime() - a.dateSubmitted.getTime())
		);
		setIsLoading(false);
	}, [session.projectSubmissions]);

	const TASKS_HEIGHT = isMobile ? 530 : 440;

	// for mobile
	function getTruncatedString(s: string) {
		return s.length > 10 ? s.slice(0, 10) + "..." : s;
	}

	const rows = mySubmissions?.map((videoRow) => {
		return (
			<React.Fragment key={videoRow.id}>
				<Table.Tr>
					{/* Submitted Date */}
					<Table.Td
						ta="center"
						fz={fzSmall}
						w={isMobile ? CW.mobile.submissionDate : CW.desktop.submissionDate}
					>
						{" "}
						{videoRow?.dateFulfilled != null
							? new Date(videoRow?.dateFulfilled).toLocaleString()
							: "N/A"}
					</Table.Td>
					{/* Username and project name*/}
					{isMobile ? (
						<Table.Td fz={fzSmall} w={CW.mobile.names} ta="center">
							{getTruncatedString(videoRow?.submissionProjectName ?? "N/A") +
								" by " +
								videoRow.projectOwner?.username}
						</Table.Td>
					) : (
						<>
							{/* Username */}
							<Table.Td fz={fzSmall} w={CW.desktop.studentName} ta="center">
								{videoRow.projectOwner?.username}
							</Table.Td>
							{/* Project Name */}
							<Table.Td fz={fzSmall} w={CW.desktop.projectName} ta="center">
								{videoRow.submissionProjectName}
							</Table.Td>
						</>
					)}
					{/* Video Link */}
					<Table.Td
						fz={fzSmall}
						w={isMobile ? CW.mobile.watchVideo : CW.desktop.watchVideo}
						ta="center"
					>
						{
							<Button
								variant="default"
								size="compact-sm"
								c={theme.colors.blue[8]}
								ta="center"
								onClick={() => {
									Utils.displayNotification(
										"Watch " + videoRow.submissionProjectName + " Complete Video",
										"",
										"green",
										<IconEye size={16} />
									);
								}}
							>
								<IconEye size={fzMed} />
							</Button>
						}
					</Table.Td>
				</Table.Tr>
			</React.Fragment>
		);
	});

	return (
		<Group>
			<Text fz={fzSmall} p={"auto"} c={theme.colors.blue[0]}>
				Watch finished videos you've contributed to here.
			</Text>
			<Box
				// width of table in dark box
				w={"100%"}
				h={TASKS_HEIGHT}
				bg={theme.colors.blue[0]}
				style={{
					borderRadius: 16,
					boxSizing: "border-box",
					overflowX: "hidden",
				}}
			>
				<Table
					horizontalSpacing="md"
					w={"100%"}
					verticalSpacing="xs"
					miw={"auto"}
					layout="fixed"
				>
					<Table.Thead>
						<Table.Tr>
							<Table.Th
								w={isMobile ? CW.mobile.submissionDate : CW.desktop.submissionDate}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW} style={{ whiteSpace: "nowrap" }}>
									Submission Date
								</Text>
							</Table.Th>
							{isMobile ? (
								<Table.Th
									w={CW.mobile.names}
									ta="center"
									style={{ whiteSpace: "nowrap" }}
								>
									<Text fz={fzSmall} fw={HEADER_FW} style={{ whiteSpace: "nowrap" }}>
										User / Project
									</Text>
								</Table.Th>
							) : (
								<>
									<Table.Th
										w={CW.desktop.studentName}
										ta="center"
										style={{ whiteSpace: "nowrap" }}
									>
										<Text fz={fzSmall} fw={HEADER_FW}>
											User Name
										</Text>
									</Table.Th>
									<Table.Th
										w={CW.desktop.projectName}
										ta="center"
										style={{ whiteSpace: "nowrap" }}
									>
										<Text fz={fzSmall} fw={HEADER_FW}>
											Project Name
										</Text>
									</Table.Th>
								</>
							)}
							<Table.Th
								w={isMobile ? CW.mobile.watchVideo : CW.desktop.watchVideo}
								ta="center"
								style={{ whiteSpace: "nowrap" }}
							>
								<Text fz={fzSmall} fw={HEADER_FW}>
									Watch Video
								</Text>
							</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{/* {skeletonRows} */}
						{isLoading ? (
							skeletonRows
						) : rows && rows.length > 0 ? (
							rows
						) : (
							<Table.Tr>
								<Table.Td colSpan={6}>
									<Text fw={500} ta="center">
										No Finished Videos
									</Text>
								</Table.Td>
							</Table.Tr>
						)}
					</Table.Tbody>
				</Table>
			</Box>
		</Group>
	);
};

export default FinishedVideos;
